/**
 * Password-reset landing on `app.<base>` (ADR-0077 S2) — the mailed link's target.
 *
 * Reads `?token=` (a short-lived `identity_reset` JWT), asks for the new password,
 * and on success adopts the fresh identity session + bounces to the workspace picker.
 * The token is effectively single-use: redeeming it bumps `token_version`, which the
 * token itself pins.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { identityApi, setIdentityToken } from "@/lib/api-onboarding";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const token = React.useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("resetPw.mismatch", "Le password non coincidono."));
      return;
    }
    setBusy(true);
    try {
      const res = await identityApi.resetConfirm({ token, password });
      setIdentityToken(res.token);
      window.location.replace("/"); // signed in → the workspace picker
    } catch {
      setError(
        t(
          "resetPw.failed",
          "Reimpostazione non riuscita: il link potrebbe essere scaduto o già usato. Richiedine uno nuovo.",
        ),
      );
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border/60 bg-surface p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-2.5">
          <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span className="text-base font-semibold tracking-tight">Tray</span>
        </div>
        <h1 className="text-lg font-semibold text-foreground">
          {t("resetPw.title", "Scegli una nuova password")}
        </h1>

        {!token ? (
          <div className="mt-4">
            <Alert tone="danger">
              {t("resetPw.noToken", "Link non valido: manca il token di reset.")}
            </Alert>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw">{t("resetPw.newPw", "Nuova password")}</Label>
              <Input
                id="rp-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw2">{t("resetPw.confirmPw", "Conferma password")}</Label>
              <Input
                id="rp-pw2"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            {error && <Alert tone="danger">{error}</Alert>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "…" : t("resetPw.submit", "Imposta la password")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
